// ═══════════════════════════════════════════════════════════════════════
//  DIRECTIVES: foreach, each, for  (aliases — single handler)
//  Self-repeating pattern: the element with each/foreach/for IS the
//  repeating template. It gets cloned N times as siblings. The parent
//  element becomes the container.
// ═══════════════════════════════════════════════════════════════════════

import { createContext } from "../context.js";
import { _watchExpr, _warn, _currentEl, _setCurrentEl } from "../globals.js";
import { evaluate, resolve } from "../evaluate.js";
import { findContext, _cloneTemplate } from "../dom.js";
import {
  registerDirective, processTree, _disposeTree,
  _buildProcessPlan, _runProcessPlan, _registryVersion,
} from "../registry.js";

// Directive and companion attribute names that must be stripped from clones
// to prevent double-execution and re-initialization.
const _LOOP_ATTRS = [
  "foreach", "each", "for", "from",
  "filter", "sort", "limit", "offset", "key", "index",
  "template",
  "animate-enter", "animate-leave", "animate-stagger", "animate-duration", "animate",
  // HTTP verb attrs — prevent clones from firing one fetch per iteration.
  "get", "post", "put", "patch", "delete",
  // Head/SEO attrs — prevent clones from clobbering document.title etc.
  "page-title", "page-description", "page-canonical", "page-jsonld",
  // Conditional attr — prevent double-init on clones.
  "else-if",
];

// Strips all loop-related directive attributes from a cloned element.
// When `stripElse` is true (default), the `else` attribute is also removed.
// It is kept when `if` or `else-if` owns the `else` attribute (Finding 8).
function _stripLoopAttrs(clone, stripElse) {
  for (let i = 0; i < _LOOP_ATTRS.length; i++) {
    clone.removeAttribute(_LOOP_ATTRS[i]);
  }
  if (stripElse !== false) clone.removeAttribute("else");
}

// Collects managed clones between the start and end comment markers.
// Returns a snapshot (array) of element nodes in the managed range.
function _getManagedClones(startMarker, endMarker) {
  const clones = [];
  let node = startMarker.nextSibling;
  while (node && node !== endMarker) {
    if (node.nodeType === 1) clones.push(node); // Element nodes only
    node = node.nextSibling;
  }
  return clones;
}

// Removes all nodes between markers (including text/comment nodes),
// disposing each element first (Safety Rule 1: dispose before removal).
// Removal itself is a single Range.deleteContents() — per-node removeChild
// dominates clear-1k profiles (native DOM bookkeeping per sibling).
function _clearManagedClones(startMarker, endMarker) {
  let node = startMarker.nextSibling;
  if (!node || node === endMarker) return;
  while (node && node !== endMarker) {
    if (node.nodeType === 1) _disposeTree(node, true);
    node = node.nextSibling;
  }
  const parent = startMarker.parentNode;
  if (parent && parent.firstChild === startMarker && parent.lastChild === endMarker) {
    // Markers span the whole parent: nuke all children in one native op
    // (fastest bulk clear), then restore the markers.
    parent.textContent = "";
    parent.appendChild(startMarker);
    parent.appendChild(endMarker);
  } else {
    const range = document.createRange();
    range.setStartAfter(startMarker);
    range.setEndBefore(endMarker);
    range.deleteContents();
  }
}

// Returns true when `el` carries a loop directive that the loop handler
// will actually claim. `foreach`/`each` always belong to the loop handler.
// A bare `for` only counts when its value is loop-shaped ("item in list")
// or the deprecated `from` companion attribute is present — this mirrors
// the loop handler's own init bail-out, so HTML's native `for` attribute
// on label/output elements is never misclassified as a loop.
export function _isLoopElement(el) {
  if (el.hasAttribute("foreach") || el.hasAttribute("each")) return true;
  if (!el.hasAttribute("for")) return false;
  const v = el.getAttribute("for") || "";
  if (/^\w+\s+in\s+\S+$/.test(v)) return true;
  return el.hasAttribute("from") && /^\w+$/.test(v);
}

// Longest increasing subsequence over the non-negative values of `arr`.
// Returns a Set of arr-indices that belong to the LIS. Entries with value
// -1 (newly created nodes) are never part of the subsequence. O(n log n)
// patience sorting with predecessor links.
function _lisIndices(arr) {
  const tails = []; // arr-indices of the smallest tail for each LIS length
  const prev = new Array(arr.length).fill(-1);
  for (let i = 0; i < arr.length; i++) {
    const v = arr[i];
    if (v < 0) continue;
    let lo = 0;
    let hi = tails.length;
    while (lo < hi) {
      const mid = (lo + hi) >> 1;
      if (arr[tails[mid]] < v) lo = mid + 1;
      else hi = mid;
    }
    if (lo > 0) prev[i] = tails[lo - 1];
    tails[lo] = i;
  }
  const result = new Set();
  let k = tails.length ? tails[tails.length - 1] : -1;
  while (k !== -1) {
    result.add(k);
    k = prev[k];
  }
  return result;
}

// Applies enter animation to a clone element.
function _applyEnterAnim(clone, animEnter, stagger, i) {
  if (!animEnter) return;
  clone.classList.add(animEnter);
  clone.addEventListener(
    "animationend",
    () => clone.classList.remove(animEnter),
    { once: true },
  );
  if (stagger) clone.style.animationDelay = i * stagger + "ms";
}

const _loopHandler = {
  priority: 10,
  init(el, name, expr) {
    const ctx = findContext(el);
    let itemName, listPath;
    const match = expr.match(/^(\w+)\s+in\s+(\S+)$/);
    if (match) {
      [, itemName, listPath] = match;
    } else {
      const fromAttr = el.getAttribute("from");
      if (!fromAttr || !/^\w+$/.test(expr)) return;
      itemName = expr;
      listPath = fromAttr;
      console.warn(
        `[NoJS] "${name}" with "from" is deprecated. Use ${name}="${itemName} in ${listPath}" instead.`,
      );
    }
    const indexName = el.getAttribute("index") || "$index";
    // When the loop element also carries `if` or `else-if`, the `else`
    // attribute belongs to the conditional chain, not the loop's empty state.
    const elseOwnedByIf = el.hasAttribute("if") || el.hasAttribute("else-if");
    const elseTpl = elseOwnedByIf ? null : el.getAttribute("else");
    const filterExpr = el.getAttribute("filter");
    const sortProp = el.getAttribute("sort");
    const limit = parseInt(el.getAttribute("limit")) || Infinity;
    const offset = parseInt(el.getAttribute("offset")) || 0;
    const tplId = el.getAttribute("template");
    const keyExpr = el.getAttribute("key");
    const animEnter = el.getAttribute("animate-enter") || el.getAttribute("animate");
    const animLeave = el.getAttribute("animate-leave");
    const stagger = parseInt(el.getAttribute("animate-stagger")) || 0;
    const animDuration = parseInt(el.getAttribute("animate-duration")) || 0;
    let prevList = null;
    // Tracks the last rendered (post-filter/sort/slice) list for delta optimization.
    let prevRendered = null;
    // True while the else template's content is rendered between the markers.
    // Guards the same-ref fast path (else nodes are not item clones) and
    // dedups redundant empty updates (rebuilding would wipe input state).
    let elseRendered = false;
    // Warn once per element when the else template id cannot be resolved.
    let elseTplWarned = false;
    // Delta optimization is disabled when filter/sort/offset modify rendered order.
    const hasPipeline = !!(filterExpr || sortProp || offset);

    // ── Self-repeating setup ──────────────────────────────────────────
    // The element with the loop directive IS the template. Insert comment
    // markers at its position, then remove it from the DOM. Clones are
    // inserted as siblings between the markers.
    const parent = el.parentNode;
    const startMarker = document.createComment("foreach");
    const endMarker = document.createComment("/foreach");
    parent.insertBefore(startMarker, el);
    parent.insertBefore(endMarker, el.nextSibling);
    // Remove el from the visible DOM — it becomes the template source.
    parent.removeChild(el);

    // key → cloned element node.
    const keyMap = new Map();

    // ── Master template + process plan ────────────────────────────────
    // Every iteration clone shares one structure, so the expensive
    // per-clone work is done once: attribute stripping and external
    // template resolution happen on a cached master, and a process plan
    // (directive matches + attribute values by child-index path) replaces
    // the per-clone TreeWalker + attribute scans. The master is rebuilt
    // when the external template element changes identity (e.g. appears
    // after a remote template load).
    let _master = null;
    let _plan = null;
    // Update-pass counter: external-template masters are revalidated at most
    // once per update() pass (see _ensureMaster).
    let _passId = 0;
    let _masterPassId = -1;

    function _ensureMaster() {
      if (_master) {
        // A directive (de)registered after this plan was built changes what
        // the master's attributes match — rebuild the plan against the
        // current registry.
        if (_plan.registryVersion !== _registryVersion) {
          _plan = _buildProcessPlan(_master);
        }
        // Inline masters derive solely from the detached original element,
        // which nothing else owns — they can never go stale. External
        // templates can be mutated in place (remote template loads fill the
        // same element), which an identity check cannot see, so re-clone
        // them once per update pass.
        if (!tplId) return;
        if (_masterPassId === _passId) return;
      }
      _masterPassId = _passId;
      const tplNow = tplId ? document.getElementById(tplId) : null;
      _master = el.cloneNode(true);
      // When `if` or `else-if` owns the `else` attr, keep it on clones
      // so the conditional directive can use it as its false-branch template.
      _stripLoopAttrs(_master, !elseOwnedByIf);
      // When template attribute is set, replace children with template content
      if (tplNow) {
        _master.innerHTML = "";
        _master.appendChild(tplNow.content.cloneNode(true));
      }
      _plan = _buildProcessPlan(_master);
    }

    // Creates a clone for one loop iteration and pairs it with its item
    // context. Directives are initialized later via _processClone — after
    // the clone is inserted, matching processTree's connected-DOM timing.
    function _makeClone(childData) {
      _ensureMaster();
      const clone = _master.cloneNode(true);
      clone.__ctx = createContext(childData, ctx);
      return clone;
    }

    function _processClone(clone) {
      _runProcessPlan(clone, _plan);
    }

    function update() {
      _passId++;
      let list = /[\[\]()\s+\-*\/!?:&|]/.test(listPath)
        ? evaluate(listPath, ctx)
        : resolve(listPath, ctx);
      // Non-array values (null, undefined, …) are treated as an empty list:
      // stale clones are cleared and the else template (if any) renders.
      if (!Array.isArray(list)) list = [];

      // Same-reference optimisation: propagate to managed clones without DOM
      // rebuild. Skipped while the else template is showing — the managed
      // nodes are template content, not item clones.
      const managedClones = _getManagedClones(startMarker, endMarker);
      if (!elseRendered && list === prevList && list.length > 0 && managedClones.length > 0) {
        for (const clone of managedClones) {
          if (clone.__ctx && clone.__ctx.$notify) clone.__ctx.$notify();
        }
        return;
      }
      prevList = list;

      // Filter — reuse a single proxy context, mutating its target per item
      if (filterExpr) {
        const filterData = { [itemName]: undefined, [indexName]: 0 };
        const filterCtx = createContext(filterData, ctx);
        const filterRaw = filterCtx.__raw;
        list = list.filter((item, i) => {
          filterRaw[itemName] = item;
          filterRaw[indexName] = i;
          // Invalidate _collectKeys cache since we bypassed the proxy setter
          delete filterRaw.__collectKeysCache;
          return !!evaluate(filterExpr, filterCtx);
        });
      }

      // Sort
      if (sortProp) {
        const desc = sortProp.startsWith("-");
        const key = desc ? sortProp.slice(1) : sortProp;
        list = [...list].sort((a, b) => {
          const va = resolve(key, a) ?? a?.[key];
          const vb = resolve(key, b) ?? b?.[key];
          // Total ordering: nullish/NaN keys sort after comparable values so
          // the comparator never returns 0 for genuinely unequal/incomparable
          // operands (which would break sort stability and ordering).
          const aBad = va == null || (typeof va === "number" && Number.isNaN(va));
          const bBad = vb == null || (typeof vb === "number" && Number.isNaN(vb));
          // Bad keys always sink to the end regardless of sort direction.
          if (aBad || bBad) {
            if (aBad && bBad) return 0;
            return aBad ? 1 : -1;
          }
          const r = va < vb ? -1 : va > vb ? 1 : 0;
          return desc ? -r : r;
        });
      }

      // Offset and limit
      list = list.slice(offset, offset + limit);

      // Empty state — show else template at marker position
      if (list.length === 0 && elseTpl) {
        // Dedup: already showing the else template — rebuilding it would
        // wipe any input state inside the template content.
        if (elseRendered) {
          prevRendered = null;
          return;
        }
        // Always clear stale item clones first — even when the else template
        // id cannot be resolved, previously rendered items must not linger.
        _clearManagedClones(startMarker, endMarker);
        keyMap.clear();
        const tplClone = _cloneTemplate(elseTpl);
        if (tplClone) {
          // Insert the else template content between markers
          parent.insertBefore(tplClone, endMarker);
          // Process the inserted nodes
          let node = startMarker.nextSibling;
          while (node && node !== endMarker) {
            if (node.nodeType === 1) processTree(node);
            node = node.nextSibling;
          }
          elseRendered = true;
        } else if (!elseTplWarned) {
          elseTplWarned = true;
          _warn(`${name}: else template "${elseTpl}" not found`, el);
        }

        prevRendered = null;
        return;
      }

      // Empty list without else template — just clear
      if (list.length === 0) {
        _clearManagedClones(startMarker, endMarker);
        keyMap.clear();
        elseRendered = false;
        prevRendered = null;
        return;
      }

      // Items are about to render — every render path below clears the
      // managed range (renderItems, keyed reconcile, delta append), so any
      // previously rendered else template content is replaced.
      elseRendered = false;

      if (keyExpr) {
        reconcileItems(list, list.length);
        prevRendered = null;
        return;
      }

      // ── Length-delta append optimization (keyless) ─────────────────────
      // When the list grew and the existing prefix items are unchanged,
      // append only the new items instead of tearing down and rebuilding
      // everything. Falls back to full rebuild when filter/sort/offset are
      // active (rendered order may differ from source order), when the list
      // shrunk, or when any existing item reference changed.
      function tryDeltaAppend() {
        if (hasPipeline) return false;
        if (!prevRendered) return false;
        const oldLen = prevRendered.length;
        const newLen = list.length;
        if (newLen <= oldLen) return false;
        const currentClones = _getManagedClones(startMarker, endMarker);
        if (currentClones.length !== oldLen) return false;
        // Shallow compare: every existing item reference must be identical.
        for (let j = 0; j < oldLen; j++) {
          if (list[j] !== prevRendered[j]) return false;
        }
        // Prefix matches — update $count and $last on existing children,
        // then append only the delta items.
        const count = newLen;
        for (let j = 0; j < oldLen; j++) {
          const childCtx = currentClones[j].__ctx;
          if (childCtx) {
            const raw = childCtx.__raw;
            raw.$count = count;
            raw.$last = false;
            // Invalidate _collectKeys cache since we bypassed the proxy setter
            delete raw.__collectKeysCache;
            childCtx.$notify();
          }
        }
        const frag = document.createDocumentFragment();
        const added = [];
        for (let i = oldLen; i < newLen; i++) {
          const childData = {
            [itemName]: list[i],
            [indexName]: i,
            $index: i,
            $count: count,
            $first: false,
            $last: i === count - 1,
            $even: i % 2 === 0,
            $odd: i % 2 !== 0,
          };
          const clone = _makeClone(childData);
          frag.appendChild(clone);
          added.push(clone);
        }
        // One DOM insertion for the whole delta, then init directives on
        // connected clones (same timing processTree had).
        parent.insertBefore(frag, endMarker);
        for (let j = 0; j < added.length; j++) {
          _processClone(added[j]);
          _applyEnterAnim(added[j], animEnter, stagger, oldLen + j);
        }
        prevRendered = list.slice();
        return true;
      }

      function renderItems() {
        const count = list.length;
        _clearManagedClones(startMarker, endMarker);
        const frag = document.createDocumentFragment();
        const added = new Array(count);
        for (let i = 0; i < count; i++) {
          const childData = {
            [itemName]: list[i],
            [indexName]: i,
            $index: i,
            $count: count,
            $first: i === 0,
            $last: i === count - 1,
            $even: i % 2 === 0,
            $odd: i % 2 !== 0,
          };
          const clone = _makeClone(childData);
          frag.appendChild(clone);
          added[i] = clone;
        }
        parent.insertBefore(frag, endMarker);
        for (let i = 0; i < count; i++) {
          _processClone(added[i]);
          _applyEnterAnim(added[i], animEnter, stagger, i);
        }
        prevRendered = list.slice();
      }

      // Animate out old items if animate-leave is set
      const currentClones = _getManagedClones(startMarker, endMarker);
      if (animLeave && currentClones.length > 0) {
        let remaining = currentClones.length;
        currentClones.forEach((clone) => {
          clone.classList.add(animLeave);
          const done = () => {
            clone.classList.remove(animLeave);
            remaining--;
            if (remaining <= 0) {
              renderItems();
            }
          };
          clone.addEventListener("animationend", done, { once: true });
          // || 0: unblocks the next render on the next tick when no CSS animation fires.
          setTimeout(done, animDuration || 0);
        });
      } else if (!tryDeltaAppend()) {
        renderItems();
      }
    }

    // Fast path for keys of the form "<itemName>.<prop>" (the common case):
    // read the property directly — no proxy context, no evaluator round-trip.
    // Dunder props fall back to the evaluator so its prototype-safety rules
    // stay authoritative.
    let keyProp = null;
    if (keyExpr) {
      const km = keyExpr.match(/^(\w+)\.(\w+)$/);
      if (km && km[1] === itemName && !["__proto__", "constructor", "prototype"].includes(km[2])) {
        keyProp = km[2];
      }
    }

    // Key-based reconciliation — applied to the final (filtered, sorted,
    // sliced) list so keys always correspond to what is actually rendered.
    function reconcileItems(list, count) {
      // On first render clear any residual content so only managed nodes appear.
      if (keyMap.size === 0) _clearManagedClones(startMarker, endMarker);

      let newOrder;
      if (keyProp) {
        newOrder = new Array(list.length);
        for (let i = 0; i < list.length; i++) {
          const item = list[i];
          newOrder[i] = { key: item == null ? undefined : item[keyProp], item, i };
        }
      } else {
        // Reuse a single proxy context for key evaluation (same pattern as filter)
        const keyData = { [itemName]: undefined, [indexName]: 0 };
        const keyCtx = createContext(keyData, ctx);
        const keyRaw = keyCtx.__raw;
        newOrder = list.map((item, i) => {
          keyRaw[itemName] = item;
          keyRaw[indexName] = i;
          // Invalidate _collectKeys cache since we bypassed the proxy setter
          delete keyRaw.__collectKeysCache;
          return { key: evaluate(keyExpr, keyCtx), item, i };
        });
      }

      const nextKeySet = new Set(newOrder.map((e) => e.key));

      // Full-replacement fast path: when no existing key survives (replace
      // rows), bulk-clear the whole managed range in one Range operation
      // instead of removing wrappers one by one.
      let anySurvives = false;
      for (const key of keyMap.keys()) {
        if (nextKeySet.has(key)) { anySurvives = true; break; }
      }
      if (!anySurvives && keyMap.size > 0) {
        _clearManagedClones(startMarker, endMarker);
        keyMap.clear();
      } else {
        for (const [key, wrapper] of keyMap) {
          if (!nextKeySet.has(key)) {
            _disposeTree(wrapper, true);
            wrapper.remove();
            keyMap.delete(key);
          }
        }
      }

      // New clones collect into one fragment (single DOM insertion before
      // the end marker — the reorder pass below puts them in place), then
      // get their directives initialized while connected.
      let frag = null;
      let added = null;
      newOrder.forEach(({ key, item, i }) => {
        const existing = keyMap.get(key);
        if (!existing) {
          const clone = _makeClone({
            [itemName]: item,
            [indexName]: i,
            $index: i,
            $count: count,
            $first: i === 0,
            $last: i === count - 1,
            $even: i % 2 === 0,
            $odd: i % 2 !== 0,
          });
          keyMap.set(key, clone);
          if (!frag) { frag = document.createDocumentFragment(); added = []; }
          frag.appendChild(clone);
          added.push({ clone, i });
        } else {
          const cloneRaw = existing.__ctx.__raw;
          // Skip the re-assign when nothing the context exposes changed:
          // $first/$last/$even/$odd derive from index and count, so comparing
          // the item ref, index, and count covers every field. The notify
          // below still fires unconditionally — the item may have been
          // mutated in place (the pinned mutate-and-slice pattern) — and
          // binding value memos turn unchanged rows into zero DOM writes.
          if (cloneRaw[itemName] !== item || cloneRaw[indexName] !== i || cloneRaw.$count !== count) {
            cloneRaw[itemName] = item;
            cloneRaw[indexName] = i;
            cloneRaw.$index = i;
            cloneRaw.$count = count;
            cloneRaw.$first = i === 0;
            cloneRaw.$last = i === count - 1;
            cloneRaw.$even = i % 2 === 0;
            cloneRaw.$odd = i % 2 !== 0;
            // Invalidate _collectKeys cache since we bypassed the proxy setter
            // (same pattern as the filter/key eval contexts above) — otherwise
            // bindings can evaluate against stale cached vals when reconcile
            // runs without an accompanying _ctxGeneration bump.
            delete cloneRaw.__collectKeysCache;
          }
          existing.__ctx.$notify();
        }
      });
      if (frag) {
        parent.insertBefore(frag, endMarker);
        for (let j = 0; j < added.length; j++) {
          _processClone(added[j].clone);
          _applyEnterAnim(added[j].clone, animEnter, stagger, added[j].i);
        }
      }

      // Reorder: ensure DOM order matches the new list order.
      // Nodes on the longest increasing subsequence of old positions stay
      // put; only out-of-place nodes are moved. A swap of two rows costs two
      // insertBefore calls instead of cascading a move for every row after
      // the first mismatch.
      const managedClones = _getManagedClones(startMarker, endMarker);
      const oldPos = new Map();
      for (let i = 0; i < managedClones.length; i++) oldPos.set(managedClones[i], i);
      const oldIndices = new Array(newOrder.length);
      for (let i = 0; i < newOrder.length; i++) {
        const node = keyMap.get(newOrder[i].key);
        oldIndices[i] = oldPos.has(node) ? oldPos.get(node) : -1;
      }
      const keep = _lisIndices(oldIndices);
      let anchor = endMarker;
      for (let i = newOrder.length - 1; i >= 0; i--) {
        const node = keyMap.get(newOrder[i].key);
        if (!keep.has(i) && node.nextSibling !== anchor) {
          parent.insertBefore(node, anchor);
        }
        anchor = node;
      }
    }

    // Redirect _currentEl to parent so _onDispose and fn._el reference a
    // connected DOM node instead of the removed template element.
    const savedEl = _currentEl;
    _setCurrentEl(parent);
    _watchExpr(listPath, ctx, update);
    update();
    _setCurrentEl(savedEl);
  },
};

registerDirective("foreach", _loopHandler);
registerDirective("each", _loopHandler);
registerDirective("for", _loopHandler);
